import { useMemo } from 'react';
import { useAuth } from '../context/auth/AuthContext';
import type { Resource, Action, PermissionMap } from '../types/permissions';

// Per Q7: if backend returns missing/empty permissions, grant view-only on every resource.
const isEmptyPermissions = (perms: PermissionMap | undefined | null): boolean => {
  if (!perms) return true;
  return Object.keys(perms).length === 0;
};

const checkPermission = (
  perms: PermissionMap | undefined | null,
  resource: Resource,
  action: Action,
): boolean => {
  if (isEmptyPermissions(perms)) {
    return action === 'view';
  }
  const resourcePerms = perms![resource];
  if (!resourcePerms) return false;
  return resourcePerms[action] === true;
};

export const usePermissions = () => {
  const { user } = useAuth();
  const permissions = user?.permissions as PermissionMap | undefined;

  return useMemo(
    () => ({
      can: (resource: Resource, action: Action): boolean =>
        checkPermission(permissions, resource, action),
      canAny: (pairs: Array<[Resource, Action]>): boolean =>
        pairs.some(([r, a]) => checkPermission(permissions, r, a)),
      canAll: (pairs: Array<[Resource, Action]>): boolean =>
        pairs.every(([r, a]) => checkPermission(permissions, r, a)),
    }),
    [permissions],
  );
};
